import React, { useMemo } from 'react';
import {
  ResponsiveContainer,
  ComposedChart,
  Bar,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
} from 'recharts';
import { BasicSpinner } from 'components';
import { formatDuration } from '../formatters';
import { buildYTicks, withAverage } from './chartData';
import TimeSpentTooltip from './TimeSpentTooltip';
import useTimeSeries from './useTimeSeries';
import {
  AVERAGE_COLOR,
  AVERAGE_LABEL,
  CHART_HEIGHT,
  COUNT_COLOR,
  COUNT_LABEL,
  EMPTY_MESSAGE,
  EMPTY_ORGANIZATION_IDS,
  EMPTY_WORKSPACE_IDS,
  GRID_COLOR,
  SURFACE_COLOR,
  TOTAL_COLOR,
  TOTAL_LABEL,
} from './constants';

export function TimeSpentChart({
  userId,
  range,
  excludedWorkspaceIds = EMPTY_WORKSPACE_IDS,
  excludedOrganizationIds = EMPTY_ORGANIZATION_IDS,
}) {
  const { buckets, loading } = useTimeSeries(
    userId,
    range,
    excludedWorkspaceIds,
    excludedOrganizationIds
  );

  const chartData = useMemo(() => withAverage(buckets), [buckets]);
  const yTicks = buildYTicks(chartData);

  // A bucket only has a card count because it has time, so "no time" covers the empty case.
  const isEmpty = !loading && buckets.every((bucket) => !bucket.time_spent_ms);

  if (loading) {
    return (
      <div className="time-spent-chart">
        <div className="d-flex justify-content-center py-4">
          <BasicSpinner />
        </div>
      </div>
    );
  }

  if (isEmpty) {
    return (
      <div className="time-spent-chart">
        <div className="d-flex justify-content-center py-4">{EMPTY_MESSAGE}</div>
      </div>
    );
  }

  return (
    <div className="time-spent-chart">
      <ResponsiveContainer width="100%" height={CHART_HEIGHT}>
        <ComposedChart data={chartData} margin={{ top: 16, right: 8, left: 0, bottom: 8 }} barGap={2}>
          <CartesianGrid vertical={false} stroke={GRID_COLOR} />
          <XAxis dataKey="label" tickLine={false} axisLine={false} />
          <YAxis
            yAxisId="duration"
            tickFormatter={formatDuration}
            ticks={yTicks}
            domain={[0, 'dataMax']}
            tickLine={false}
            axisLine={false}
            width={68}
            label={{
              value: 'Time',
              angle: -90,
              position: 'insideLeft',
              className: 'time-spent-chart__axis-label',
            }}
          />
          <YAxis
            yAxisId="count"
            orientation="right"
            allowDecimals={false}
            domain={[0, 'dataMax']}
            tickLine={false}
            axisLine={false}
            width={52}
            label={{
              value: 'Cards',
              angle: 90,
              position: 'insideRight',
              className: 'time-spent-chart__axis-label',
            }}
          />
          <Tooltip content={<TimeSpentTooltip />} cursor={{ fill: 'rgba(0,0,0,0.04)' }} />
          <Legend verticalAlign="bottom" />
          <Bar
            yAxisId="duration"
            name={AVERAGE_LABEL}
            dataKey="average_time_per_card_ms"
            fill={AVERAGE_COLOR}
            radius={[4, 4, 0, 0]}
            maxBarSize={24}
          />
          <Bar
            yAxisId="duration"
            name={TOTAL_LABEL}
            dataKey="time_spent_ms"
            fill={TOTAL_COLOR}
            radius={[4, 4, 0, 0]}
            maxBarSize={24}
          />
          <Line
            yAxisId="count"
            name={COUNT_LABEL}
            type="linear"
            dataKey="time_tracked_cards_count"
            stroke={COUNT_COLOR}
            strokeWidth={2}
            strokeLinecap="round"
            strokeLinejoin="round"
            dot={{ r: 4, fill: COUNT_COLOR, stroke: SURFACE_COLOR, strokeWidth: 2 }}
            activeDot={{ r: 6, fill: COUNT_COLOR, stroke: SURFACE_COLOR, strokeWidth: 2 }}
          />
        </ComposedChart>
      </ResponsiveContainer>
    </div>
  );
}

export default TimeSpentChart;
